import { useEffect, useState } from 'react';

import { Box } from '@bonusly/design-system';

/**
 * Blur layers, weakest first. Every layer spans the full height and fades out
 * at its own depth, so the composite ramps from heavy blur at the top to none
 * at the bottom. Adjacent clipped bands would show seams where they meet.
 */
const PROGRESSIVE_BLUR_LAYERS = [
	{ blur: 1, fadeStart: 70, fadeEnd: 100 },
	{ blur: 2, fadeStart: 52, fadeEnd: 82 },
	{ blur: 3, fadeStart: 34, fadeEnd: 64 },
	{ blur: 4, fadeStart: 16, fadeEnd: 46 },
	{ blur: 5, fadeStart: 0, fadeEnd: 28 },
] as const;

const SURFACE_FADE_MASK = 'linear-gradient(to bottom, rgba(0, 0, 0, 0.92) 0%, rgba(0, 0, 0, 0.85) 45%, transparent 100%)';
const progressiveLayerMask = (fadeStart: number, fadeEnd: number): string =>
	`linear-gradient(to bottom, black 0%, black ${fadeStart}%, transparent ${fadeEnd}%, transparent 100%)`;

/**
 * Width the platform's classic scrollbar occupies, or 0 for overlay scrollbars
 * (macOS/iOS default). Paired with `scrollbar-gutter: stable` on the scroll
 * container, this is the exact strip the overlay must leave alone so the
 * scrollbar stays crisp instead of being blurred along with the content.
 */
const measureScrollbarWidth = (): number => {
	const probe = document.createElement('div');
	probe.style.cssText = 'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;';
	document.body.appendChild(probe);
	const width = probe.offsetWidth - probe.clientWidth;
	probe.remove();
	return width;
};

const useScrollbarWidth = (): number => {
	const [width, setWidth] = useState(0);

	useEffect(() => {
		setWidth(measureScrollbarWidth());
	}, []);

	return width;
};

interface BizyProgressiveTopBlurProps {
	height?: number | string;
	/** Colour the fade resolves to; pass the host surface when it isn't `bg.surface`. */
	surfaceColor?: string;
}

/** Progressively blurs and fades scrolling content beneath the chat header. */
export function BizyProgressiveTopBlur({ height = '100%', surfaceColor = 'bg.surface' }: BizyProgressiveTopBlurProps) {
	const scrollbarWidth = useScrollbarWidth();

	return (
		<Box
			aria-hidden
			position="absolute"
			top={0}
			left={0}
			right={`${scrollbarWidth}px`}
			height={height}
			pointerEvents="none"
			// Blur layers are decorative; drop them (leaving the plain fade) when the
			// viewer has asked for reduced transparency.
			sx={{ '@media (prefers-reduced-transparency: reduce)': { '& > [data-blur-layer]': { display: 'none' } } }}
		>
			{PROGRESSIVE_BLUR_LAYERS.map(({ blur, fadeStart, fadeEnd }) => {
				const mask = progressiveLayerMask(fadeStart, fadeEnd);
				return (
					<Box
						key={blur}
						data-blur-layer
						position="absolute"
						inset={0}
						backdropFilter={`blur(${blur}px)`}
						sx={{
							WebkitBackdropFilter: `blur(${blur}px)`,
							maskImage: mask,
							WebkitMaskImage: mask,
						}}
					/>
				);
			})}
			<Box
				position="absolute"
				inset={0}
				bg={surfaceColor}
				sx={{
					maskImage: SURFACE_FADE_MASK,
					WebkitMaskImage: SURFACE_FADE_MASK,
				}}
			/>
		</Box>
	);
}
